import { SentryProvider } from '@/lib/sentry'; // Load Sentry before anything else
import * as Sentry from '@sentry/react';
import App from '@/App.tsx';
import '@/index.css';
import { WORKOS_CLIENT_ID } from '@/lib/constants';
import { AuthKitProvider } from '@auth';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { PostHogConfig } from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { toast } from 'sonner';
import { ApiHttpError } from './lib/apiHttpError';
import { Button } from './components/ui/button';

const postHogOptions: Partial<PostHogConfig> = {
  api_host: 'https://us.i.posthog.com',
  autocapture: true,
  capture_pageview: 'history_change',
  persistence: 'localStorage+cookie',
  cross_subdomain_cookie: true,
  capture_exceptions: true,
  capture_heatmaps: true,
  capture_performance: true,
  ip: true,
  name: 'enrich app',
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      throwOnError: true, // by default, throw errors to the error boundary
      retry: (failureCount, error) => {
        if (error instanceof ApiHttpError) {
          if (error.response.status >= 400 && error.response.status < 500) {
            return false;
          }
        }
        console.warn(`Connection error ${failureCount}: ${error.message}`);
        switch (failureCount) {
          case 0:
          case 1:
            break;
          case 2:
            toast.warning('Connection error. Retrying...', { id: 'retry-warning', duration: 4000 });
            break;
          case 3:
            toast.warning('Attempting to reconnect...', { id: 'retry-warning', duration: 10000 });
            break;
          default:
            toast.dismiss('retry-warning');
            break;
        }
        return failureCount < 4;
      },
    },
    mutations: {
      // By default, show an error toast when a mutation errors
      onError: async (err) => {
        if (err instanceof ApiHttpError) {
          const isAdminApi = err.response.url.includes('/admin/');
          const responseBody = await err.response.json();
          const copyLogsAction = isAdminApi
            ? {
                action: (
                  <Button
                    variant="enrich_secondary"
                    onClick={() =>
                      navigator.clipboard.writeText(JSON.stringify(responseBody, null, 4))
                    }
                  >
                    Copy logs
                  </Button>
                ),
              }
            : {};
          if (err.response.status >= 500) {
            toast.error('Unexpected error', {
              id: 'mutation-error',
              description: `${err.message} (${err.response.status})`,
              ...copyLogsAction,
            });
            return;
          }
          if (err.response.status < 500) {
            toast.error('Error processing your request', {
              id: 'mutation-error',
              description: `${err.message} (${err.response.status})`,
              ...copyLogsAction,
            });
            return;
          }
        }
        toast.error('Unexpected error', {
          id: 'mutation-error',
          description: `Error: "${err.message}"`,
        });
      },
    },
  },
});

createRoot(document.getElementById('root')!, {
  // Callback called when an error is thrown and not caught by an ErrorBoundary.
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.warn('Uncaught error', error, errorInfo.componentStack);
  }),
  // Callback called when React catches an error in an ErrorBoundary.
  onCaughtError: Sentry.reactErrorHandler(),
  // Callback called when React automatically recovers from errors.
  onRecoverableError: Sentry.reactErrorHandler(),
}).render(
  <StrictMode>
    <SentryProvider>
      <PostHogProvider apiKey={import.meta.env.VITE_POSTHOG_KEY} options={postHogOptions}>
        <AuthKitProvider clientId={WORKOS_CLIENT_ID} devMode={true}>
          <QueryClientProvider client={queryClient}>
            <App />
          </QueryClientProvider>
        </AuthKitProvider>
      </PostHogProvider>
    </SentryProvider>
  </StrictMode>,
);
